import type { ConditionLeaf, LogicalOp } from '../types/rule';

/**
 * TRADUZ OPERADOR LÓGICO DE INGLÊS PARA PORTUGUÊS
 */
function translateOperator(operator?: LogicalOp | string): string {
  if (!operator) return 'E';

  const upperOp = operator.toUpperCase();
  if (upperOp === 'AND') return 'E';
  if (upperOp === 'OR') return 'OU';

  // Já está em português
  return operator;
}

/**
 * GERA PREVIEW TEXTUAL DA LÓGICA DE CONDIÇÕES
 *
 * Recebe lista flat de condições e retorna string legível:
 * Exemplo: "((C1 E C2) OU C3)"
 */
export function generateConditionPreview(conditions: ConditionLeaf[]): string {
  if (conditions.length === 0) return 'Nenhuma condição';
  if (conditions.length === 1) return 'C1';

  // Construir preview recursivamente
  let result = 'C1';

  for (let i = 1; i < conditions.length; i++) {
    const operator = translateOperator(conditions[i].logicalOperator);
    const conditionLabel = `C${i + 1}`;

    // Agrupar: (resultado_anterior OPERADOR nova_condição)
    result = `(${result} ${operator} ${conditionLabel})`;
  }

  return result;
}

/**
 * GERA PREVIEW DETALHADO COM LABELS DOS CAMPOS
 *
 * Versão mais amigável mostrando os nomes dos campos
 */
export function generateConditionPreviewDetailed(conditions: ConditionLeaf[]): string {
  if (conditions.length === 0) return 'Nenhuma condição';

  const getLabel = (cond: ConditionLeaf, index: number) => {
    return cond.meta?.field_label || `Condição ${index + 1}`;
  };

  if (conditions.length === 1) return getLabel(conditions[0], 0);

  let result = getLabel(conditions[0], 0);

  for (let i = 1; i < conditions.length; i++) {
    const operator = translateOperator(conditions[i].logicalOperator);
    const label = getLabel(conditions[i], i);

    result = `(${result} ${operator} ${label})`;
  }

  return result;
}

/**
 * CALCULA PROFUNDIDADE DE AGRUPAMENTO PARA CADA CONDIÇÃO
 *
 * Retorna array com o nível de aninhamento de cada condição.
 * Usado para renderizar bordas visuais.
 *
 * Exemplo:
 * Input: [C1, C2(E), C3(OU)]
 * Output: [0, 1, 0]  // C1 e C3 no nível 0, C2 aninhado no nível 1
 */
export function calculateConditionDepths(conditions: ConditionLeaf[]): number[] {
  if (conditions.length === 0) return [];
  if (conditions.length === 1) return [0];

  const depths: number[] = [0]; // Primeira condição sempre nível 0
  let currentDepth = 0;

  for (let i = 1; i < conditions.length; i++) {
    const operator = conditions[i].logicalOperator;
    const prevOperator = i > 1 ? conditions[i - 1].logicalOperator : null;

    // Se operador muda, ajusta profundidade
    if (operator !== prevOperator && prevOperator !== null) {
      // Mudança de operador = novo nível de agrupamento
      currentDepth = 0; // Reset para próximo grupo
    } else if (operator === prevOperator || i === 1) {
      // Mesmo operador = aumenta profundidade
      currentDepth = 1;
    }

    depths.push(currentDepth);
  }

  return depths;
}

/**
 * RETORNA COR DA BORDA BASEADA NA PROFUNDIDADE
 */
export function getBorderColor(depth: number, theme: any): string {
  const colors = [
    theme.primary, // Nível 0: Cor primária
    theme.secondary || theme.primary + '80', // Nível 1: Secundária ou primary opaco
    theme.text.secondary, // Nível 2+: Texto secundário
  ];

  return colors[Math.min(depth, colors.length - 1)];
}

/**
 * VALIDA SE PODE ADICIONAR MAIS CONDIÇÕES
 */
export const MAX_CONDITIONS = 7;
export const SOFT_LIMIT_CONDITIONS = 5;

export function validateCanAddCondition(currentCount: number): {
  canAdd: boolean;
  warning?: string;
  error?: string;
} {
  if (currentCount >= MAX_CONDITIONS) {
    return {
      canAdd: false,
      error: `Máximo de ${MAX_CONDITIONS} condições atingido. Para lógicas mais complexas, considere dividir em múltiplas regras.`,
    };
  }

  if (currentCount >= SOFT_LIMIT_CONDITIONS) {
    return {
      canAdd: true,
      warning: `Você está criando uma regra complexa (${currentCount} condições). Recomendamos simplificar para facilitar manutenção.`,
    };
  }

  return { canAdd: true };
}

/**
 * GERA TEXTO EXPLICATIVO DO AGRUPAMENTO
 */
export function getGroupingExplanation(conditionCount: number): string {
  if (conditionCount <= 1) return '';
  if (conditionCount === 2) return 'As 2 condições serão agrupadas.';

  return `As ${conditionCount} condições serão agrupadas automaticamente 2 a 2, criando uma hierarquia de validação.`;
}
